<template>
  <form @submit.prevent="submit()" class="space-y-6">
    <div class="flex flex-col gap-3">
      <FirmwareSelector
        v-model="firmwareSelection"
        @firmware-selected="onFirmwareSelected"
        @url-changed="onUrlChanged"
      />
    </div>
    <div class="grid grid-cols-1 gap-y-4 md:grid-cols-2 md:gap-x-6">
      <InfuseField :label="t('form.retrieveDate.label')" size="small" :helpText="t('form.retrieveDate.helpText')">
        <InfuseDatePicker v-model="payload.retrieveDate" required />
      </InfuseField>

      <InfuseField :label="t('form.numRetries.label')" size="small" :helpText="t('form.numRetries.helpText')" optional>
        <InfuseInput v-model="payload.numRetries" type="number" size="small" :min="0" />
      </InfuseField>

      <InfuseField
        :label="t('form.retryInterval.label')"
        size="small"
        :helpText="t('form.retryInterval.helpText')"
        optional
      >
        <InfuseInput v-model="payload.retryInterval" type="number" size="small" :min="0" />
      </InfuseField>

      <InfuseField
        v-if="props.protocol === Protocol.OCPP201"
        :label="t('form.installAt.label')"
        size="small"
        :helpText="t('form.installAt.helpText')"
        optional
      >
        <InfuseDatePicker v-model="payload.installAt" />
      </InfuseField>

      <InfuseField
        v-if="props.protocol === Protocol.OCPP201"
        :label="t('form.signature.label')"
        size="small"
        :helpText="t('form.signature.helpText')"
        optional
      >
        <InfuseInput v-model="payload.signature" type="text" size="small" />
      </InfuseField>

      <InfuseField
        v-if="props.protocol === Protocol.OCPP201"
        :label="t('form.signingCertificate.label')"
        size="small"
        :helpText="t('form.signingCertificate.helpText')"
        optional
      >
        <InfuseTextArea v-model="payload.signingCertificate" size="small" />
      </InfuseField>
    </div>
    <div>
      <InfuseButton
        v-permission="{ id: commandPermissions.UPDATE_FIRMWARE, hide: false }"
        class="w-full md:w-48"
        type="submit"
        :disabled="(!payload.location?.trim() && !payload.firmwareId) || !payload.retrieveDate"
      >
        {{ t('action.updateFirmware') }}
      </InfuseButton>
    </div>
  </form>
</template>

<script setup lang="ts">
import { commandPermissions } from '@infuse/api/cpms/operator';
import { Protocol } from '@infuse/cpms/charging-station/store';
import { vPermission } from '@infuse/iam/permission/directives';
import { InfuseButton, InfuseDatePicker, InfuseField, InfuseInput, InfuseTextArea } from '@infuse/ui/components';
import { ref, unref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { UpdateFirmwareEventPayload } from './types/UpdateFirmwareEventPayload';
import { Firmware } from '@infuse/api/cpms/firmware-management';
import { FirmwareSelector } from '@infuse/cpms/location/ui';

interface Emits {
  (e: 'update', payload: UpdateFirmwareEventPayload): void;
}

const { t } = useI18n();

const emit = defineEmits<Emits>();

interface Props {
  protocol?: Protocol;
}

const props = withDefaults(defineProps<Props>(), {
  protocol: Protocol.OCPPJ16
});

const payload = ref<UpdateFirmwareEventPayload>({
  location: undefined,
  firmwareId: undefined,
  retrieveDate: new Date(),
  numRetries: undefined,
  retryInterval: undefined,
  installAt: undefined,
  signingCertificate: undefined,
  signature: undefined
});

interface FirmwareSelectionData {
  selectionMode: 'select' | 'url';
  firmware?: Firmware;
  customFirmwareUrl?: string;
}

const firmwareSelection = ref<FirmwareSelectionData>({
  selectionMode: 'select',
  firmware: undefined,
  customFirmwareUrl: ''
});

// Watch for changes in firmwareSelection and update payload accordingly
watch(
  () => firmwareSelection.value,
  (newSelection) => {
    switch (newSelection.selectionMode) {
      case 'select':
        payload.value.firmwareId = newSelection.firmware?.id;
        payload.value.location = undefined;
        break;
      case 'url': {
        const normalized = newSelection.customFirmwareUrl?.trim();
        payload.value.location = normalized || undefined;
        payload.value.firmwareId = undefined;
        break;
      }
    }
  },
  { immediate: true, deep: true }
);

function submit() {
  const rawPayload = unref(payload);

  let finalPayload;
  if (firmwareSelection.value.selectionMode === 'select') {
    finalPayload = {
      ...rawPayload,
      firmwareId: firmwareSelection.value.firmware?.id,
      location: undefined
    };
  } else {
    const location = firmwareSelection.value.customFirmwareUrl?.trim() || undefined;
    finalPayload = {
      ...rawPayload,
      location,
      firmwareId: undefined
    };
  }

  if ((!finalPayload.location && !finalPayload.firmwareId) || !finalPayload.retrieveDate) {
    return;
  }

  emit('update', finalPayload);
}

function onFirmwareSelected(firmware?: Firmware) {
  firmwareSelection.value.firmware = firmware;
  if (firmware) {
    firmwareSelection.value.customFirmwareUrl = '';
  }
}

function onUrlChanged(url: string) {
  firmwareSelection.value.customFirmwareUrl = url;
  if (firmwareSelection.value.selectionMode === 'url') {
    payload.value.location = url.trim() || undefined;
    payload.value.firmwareId = undefined;
  }
}
</script>

<i18n>
{
  "en": {
    "action": {
      "updateFirmware": "Update firmware"
    },
    "form": {
      "retrieveDate": {
        "label": "Retrieve date",
        "helpText": "Date and time at which the firmware shall be retrieved."
      },
      "numRetries": {
        "label": "Number of retries",
        "helpText": "Number of times the Charging Station must try to download the firmware before giving up. Leave empty to let the charging station decide."
      },
      "retryInterval": {
        "label": "Retry interval",
        "helpText": "Time in seconds between each retry."
      },
      "installAt": {
        "label": "Install date and time",
        "helpText": "Date and time at which the firmware shall be installed."
      },
      "signingCertificate": {
        "label": "Signing certificate",
        "helpText": "Certificate used to sign the firmware."
      },
      "signature": {
        "label": "Signature",
        "helpText": "Base64 encoded firmware signature."
      }
    }
  },
  "nl": {
    "action": {
      "updateFirmware": "Firmware updaten"
    },
    "form": {
      "retrieveDate": {
        "label": "Ophaaldatum",
        "helpText": "Datum en tijd waarop de firmware moet worden opgehaald."
      },
      "numRetries": {
        "label": "Aantal pogingen",
        "helpText": "Aantal keren dat de laadpaal de firmware probeert te downloaden. Laat dit veld leeg om de laadpaal het aantal pogingen te laten bepalen."
      },
      "retryInterval": {
        "label": "Pauze tussen pogingen",
        "helpText": "Tijd in seconden tussen elke poging."
      },
      "installAt": {
        "label": "Installatiedatum en -tijd",
        "helpText": "Datum en tijd waarop de firmware moet worden geïnstalleerd."
      },
      "signingCertificate": {
        "label": "Ondertekeningscertificaat",
        "helpText": "Certificaat dat wordt gebruikt om de firmware te ondertekenen."
      },
      "signature": {
        "label": "Handtekening",
        "helpText": "Base64 gecodeerde firmwarehandtekening."
      }
    }
  }
}
</i18n>
